import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import SEO from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 120px var(--space-md) 80px;
`;

const BackLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: var(--space-sm);
  color: var(--primary-600);
  text-decoration: none;
  margin-bottom: var(--space-xl);
  
  &:hover {
    color: var(--primary-700);
  }
`;

const Title = styled.h1`
  font-size: 2.5rem;
  margin-bottom: var(--space-lg);
  color: var(--secondary-900);
`;

const Content = styled.div`
  line-height: 1.8;
  font-size: 1.125rem;
  color: var(--secondary-700);

  h2 {
    margin: var(--space-2xl) 0 var(--space-lg);
    color: var(--secondary-900);
    font-size: 1.75rem;
  }

  h3 {
    margin: var(--space-xl) 0 var(--space-md);
    color: var(--secondary-900);
    font-size: 1.25rem;
  }

  p {
    margin-bottom: var(--space-lg);
  }

  ul, ol {
    margin-bottom: var(--space-lg);
    padding-left: var(--space-xl);
  }

  li {
    margin-bottom: var(--space-sm);
  }

  strong {
    color: var(--secondary-900);
    font-weight: 600;
  }

  .contact-info {
    background: var(--secondary-50);
    padding: var(--space-lg);
    border-radius: var(--radius-md);
    margin: var(--space-lg) 0;
    border-left: 4px solid var(--primary-600);
  }

  .last-updated {
    font-style: italic;
    color: var(--secondary-500);
    margin-top: var(--space-2xl);
    padding-top: var(--space-lg);
    border-top: 1px solid var(--secondary-200);
  }
`;

const PrivacyPolicy = () => {
  const { t } = useTranslation();
  
  return (
    <>
      <SEO 
        title={t('privacy.title')}
        description="Privacy Policy for ScrumNX - Learn how we collect, use, and protect your personal information."
        keywords="Privacy Policy, Data Protection, GDPR, Personal Data, Information Security"
      />
      <Breadcrumbs />
      <Container>
        <BackLink to="/">
          <FaArrowLeft /> {t('commonPages.backToHome')}
        </BackLink>

        <Title>{t('privacy.title')}</Title>

        <Content>
          <p>
            {t('privacy.intro')}
          </p>

          <h2>1. {t('privacy.informationWeCollect')}</h2>

          <h3>1.1 {t('privacy.personalInformation')}</h3>
          <p>{t('privacy.personalInformationText')}</p>
          <ul>
            <li><strong>{t('privacy.contactInfo')}</strong> {t('privacy.contactInfoDetails')}</li>
            <li><strong>{t('privacy.professionalInfo')}</strong> {t('privacy.professionalInfoDetails')}</li>
            <li><strong>{t('privacy.communicationData')}</strong> {t('privacy.communicationDataDetails')}</li>
            <li><strong>{t('privacy.websiteUsageData')}</strong> {t('privacy.websiteUsageDataDetails')}</li>
          </ul>

          <h3>1.2 {t('privacy.howWeCollect')}</h3>
          <ul>
            <li><strong>{t('privacy.directlyFromYou')}</strong> {t('privacy.directlyFromYouText')}</li>
            <li><strong>{t('privacy.automatically')}</strong> {t('privacy.automaticallyText')}</li>
            <li><strong>{t('privacy.fromThirdParties')}</strong> {t('privacy.fromThirdPartiesText')}</li>
          </ul>

          <h2>2. {t('privacy.howWeUse')}</h2>

          <p>{t('privacy.howWeUseText')}</p>
          <ul>
            <li><strong>{t('privacy.providingServices')}</strong> {t('privacy.providingServicesText')}</li>
            <li><strong>{t('privacy.communication')}</strong> {t('privacy.communicationText')}</li>
            <li><strong>{t('privacy.marketing')}</strong> {t('privacy.marketingText')}</li>
            <li><strong>{t('privacy.improvingServices')}</strong> {t('privacy.improvingServicesText')}</li>
            <li><strong>{t('privacy.legalCompliance')}</strong> {t('privacy.legalComplianceText')}</li>
          </ul>

          <h2>3. {t('privacy.legalBasis')}</h2>

          <p>{t('privacy.legalBasisText')}</p>
          <ul>
            <li><strong>{t('privacy.consent')}</strong> {t('privacy.consentText')}</li>
            <li><strong>{t('privacy.contract')}</strong> {t('privacy.contractText')}</li>
            <li><strong>{t('privacy.legitimateInterest')}</strong> {t('privacy.legitimateInterestText')}</li>
            <li><strong>{t('privacy.legalObligation')}</strong> {t('privacy.legalObligationText')}</li>
          </ul>

          <h2>4. {t('privacy.dataSharing')}</h2>

          <p>{t('privacy.dataSharingText')}</p>
          <ul>
            <li><strong>{t('privacy.serviceProviders')}</strong> {t('privacy.serviceProvidersText')}</li>
            <li><strong>{t('privacy.legalRequirements')}</strong> {t('privacy.legalRequirementsText')}</li>
            <li><strong>{t('privacy.businessTransfers')}</strong> {t('privacy.businessTransfersText')}</li>
            <li><strong>{t('privacy.withYourConsent')}</strong> {t('privacy.withYourConsentText')}</li>
          </ul>

          <h2>5. {t('privacy.dataSecurity')}</h2>

          <p>{t('privacy.dataSecurityText')}</p>
          <ul>
            <li>{t('privacy.encryption')}</li>
            <li>{t('privacy.securityAssessments')}</li>
            <li>{t('privacy.accessControls')}</li>
            <li>{t('privacy.employeeTraining')}</li>
          </ul>

          <h2>6. {t('privacy.dataRetention')}</h2>

          <p>{t('privacy.dataRetentionText')}</p>
          <ul>
            <li>{t('privacy.typeOfInfo')}</li>
            <li>{t('privacy.purpose')}</li>
            <li>{t('privacy.legalRequirements')}</li>
            <li>{t('privacy.consentPreferences')}</li>
          </ul>

          <h2>7. {t('privacy.yourRights')}</h2>

          <p>{t('privacy.yourRightsText')}</p>
          <ul>
            <li><strong>{t('privacy.access')}</strong> {t('privacy.accessText')}</li>
            <li><strong>{t('privacy.rectification')}</strong> {t('privacy.rectificationText')}</li>
            <li><strong>{t('privacy.erasure')}</strong> {t('privacy.erasureText')}</li>
            <li><strong>{t('privacy.portability')}</strong> {t('privacy.portabilityText')}</li>
            <li><strong>{t('privacy.objection')}</strong> {t('privacy.objectionText')}</li>
            <li><strong>{t('privacy.restriction')}</strong> {t('privacy.restrictionText')}</li>
            <li><strong>{t('privacy.withdrawConsent')}</strong> {t('privacy.withdrawConsentText')}</li>
          </ul>

          <h2>8. {t('privacy.cookies')}</h2>

          <p>{t('privacy.cookiesText')}</p>
          <ul>
            <li>{t('privacy.rememberPreferences')}</li>
            <li>{t('privacy.analyzeTraffic')}</li>
            <li>{t('privacy.personalizedContent')}</li>
            <li>{t('privacy.improveFunctionality')}</li>
          </ul>

          <p>{t('privacy.cookieControl')}</p>

          <h2>9. {t('privacy.internationalTransfers')}</h2>

          <p>{t('privacy.internationalTransfersText')}</p>

          <h2>10. {t('privacy.childrenPrivacy')}</h2>

          <p>{t('privacy.childrenPrivacyText')}</p>

          <h2>11. {t('privacy.changesToPolicy')}</h2>

          <p>{t('privacy.changesToPolicyText')}</p>

          <h2>12. {t('privacy.contactInfo')}</h2>

          <div className="contact-info">
            <p><strong>{t('privacy.contactInfoText')}</strong></p>
            <p>
              <strong>Anass Jdaa</strong><br />
              Bahnhofstr. 76<br />
              25715 Eddelak<br />
              Germany<br />
              {t('common.email')}: contact@scrumnx.com
            </p>
          </div>

          <div className="last-updated">
            <p><strong>{t('commonPages.lastUpdated')}:</strong> January 15, 2026</p>
          </div>
        </Content>
      </Container>
    </>
  );
};

export default PrivacyPolicy; 